import { useNavigate } from 'react-router-dom';
import { theme } from '../theme.js';
import { useOutbox, pendingForRun, dismissConflict, keepMine, flush } from '../lib/offline/outbox.js';
import { Icon } from './Icon.js';

const STATUS_WORD: Record<string, string> = { ok: 'OK', missing: 'Missing', broken: 'Broken', attention: 'Attention', rectified: 'Rectified' };

/**
 * On the check screens: changes saved on this phone that haven't reached the
 * server yet, and any that couldn't be applied because someone else changed
 * the item first. Hidden when everything is in.
 */
export function SyncBanner({ runId }: { runId: string }) {
  const nav = useNavigate();
  const s = useOutbox();
  const pending = pendingForRun(s, runId);
  const conflicts = s.conflicts.filter((c) => c.runId === runId);
  const offline = !s.online || s.stalled;
  if (!pending && !conflicts.length && s.online) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 16px', background: theme.bg, flexShrink: 0 }}>
      {(pending > 0 || !s.online) && (
        <div role="status" style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 10, fontSize: 13,
          background: offline ? theme.status.warn.bg : theme.status.service.bg,
          color: offline ? theme.status.warn.fg : theme.status.service.fg,
        }}>
          <Icon name={offline ? 'warning' : 'clock'} size={15} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0 }}>
            {offline
              ? pending
                ? <><strong>No signal</strong> — {pending} change{pending > 1 ? 's' : ''} saved on this phone. {pending > 1 ? 'They’ll' : 'It’ll'} send when you’re back online.</>
                : <><strong>No signal</strong> — keep going; changes are saved on this phone.</>
              : <>Sending {pending} change{pending > 1 ? 's' : ''}…</>}
          </span>
          {offline && pending > 0 && s.online && (
            <button onClick={() => void flush()} style={smallBtn}>Retry</button>
          )}
        </div>
      )}
      {conflicts.map((c) => (
        <div key={c.id} role="alert" style={{ padding: '8px 12px', borderRadius: 10, fontSize: 13, background: theme.status.danger.bg, color: theme.status.danger.fg }}>
          <div>
            <strong>{c.label}:</strong> {c.message}, so your change
            {c.mine.status ? ` (${STATUS_WORD[c.mine.status] ?? c.mine.status})` : ''} wasn’t saved.
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            <button onClick={() => { dismissConflict(c.id); nav(`/checks/${c.runId}/items/${c.itemId}`); }} style={smallBtn}>See theirs</button>
            <button onClick={() => keepMine(c)} style={smallBtn}>Use mine</button>
            <button onClick={() => dismissConflict(c.id)} style={{ ...smallBtn, background: 'none', border: 'none', textDecoration: 'underline' }}>Keep theirs</button>
          </div>
        </div>
      ))}
    </div>
  );
}

const smallBtn: React.CSSProperties = {
  padding: '5px 10px', borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface,
  color: theme.text, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font, flexShrink: 0,
};
